import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { pageTitle } from '../../helper';
import {
  formatDisplayDate,
  formatMoney,
  paymentApiUrl,
  readJsonResponse,
} from '../../utils/payment';
import Div from '../Div';
import PageHeading from '../PageHeading';
import Spacing from '../Spacing';

const contentByStatus = {
  SUCCESS: {
    title: 'Payment Successful',
    pageLinkText: 'Success',
  },
  FAILED: {
    title: 'Payment Failed',
    pageLinkText: 'Failed',
  },
  CANCELLED: {
    title: 'Payment Cancelled',
    pageLinkText: 'Cancelled',
  },
};

export default function PaymentResultPage({ status }) {
  const [params] = useSearchParams();
  const reference = params.get('reference');
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const config = contentByStatus[status];

  useEffect(() => {
    pageTitle(config.title);
    window.scrollTo(0, 0);
  }, [config.title]);

  useEffect(() => {
    if (!reference) {
      setError('Reference number is missing.');
      return;
    }

    let isActive = true;
    fetch(paymentApiUrl(`/api/payments/${reference}`))
      .then(readJsonResponse)
      .then((data) => {
        if (isActive) {
          setPayment(data.payment);
        }
      })
      .catch((fetchError) => {
        if (isActive) {
          setError(fetchError.message);
        }
      });

    return () => {
      isActive = false;
    };
  }, [reference]);

  return (
    <>
      <PageHeading
        title={config.title}
        bgSrc="/images/service_hero_bg.jpeg"
        pageLinkText={config.pageLinkText}
      />
      <Spacing lg="120" md="70" />
      <Div className="container">
        <Div className="row justify-content-center">
          <Div className="col-lg-8">
            <Div
              className="cs-bg_color_1"
              style={{ padding: 32, borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {error ? (
                <p className="cs-m0" style={{ color: '#ff6b6b' }}>{error}</p>
              ) : null}
              {!payment && !error ? <p className="cs-m0">Loading payment details...</p> : null}
              {payment ? (
                <>
                  {status === 'SUCCESS' ? (
                    <>
                      <h2 className="cs-font_38 cs-m0">Payment Successful</h2>
                      <Spacing lg="24" md="18" />
                      <p className="cs-m0">Reference Number: {payment.referenceNumber}</p>
                      <p className="cs-m0">Payment Reference: {payment.paymentReference || '-'}</p>
                      <p className="cs-m0">Gateway Transaction ID: {payment.gatewayTransactionId || '-'}</p>
                      <p className="cs-m0">Amount: {formatMoney(payment.amount, payment.currency)}</p>
                      <p className="cs-m0">Date: {formatDisplayDate(payment.updatedAt || payment.createdAt)}</p>
                    </>
                  ) : null}
                  {status === 'FAILED' ? (
                    <>
                      <h2 className="cs-font_38 cs-m0">Your transaction was not successful.</h2>
                      <Spacing lg="24" md="18" />
                      <p className="cs-m0">Reason: {payment.failureReason || 'Payment could not be completed.'}</p>
                      <p className="cs-m0">Reference Number: {payment.referenceNumber}</p>
                      <Spacing lg="16" md="12" />
                      <p className="cs-m0">Please contact support with this reference number.</p>
                    </>
                  ) : null}
                  {status === 'CANCELLED' ? (
                    <>
                      <h2 className="cs-font_38 cs-m0">Payment Cancelled</h2>
                      <Spacing lg="24" md="18" />
                      <p className="cs-m0">The customer cancelled the hosted checkout process before completion.</p>
                      <p className="cs-m0">Reference Number: {payment.referenceNumber}</p>
                    </>
                  ) : null}
                </>
              ) : null}
            </Div>
            <Spacing lg="30" md="20" />
            <Link to="/payment" className="cs-btn cs-style1" style={{ display: 'inline-flex' }}>
              <span>Back to payment</span>
            </Link>
          </Div>
        </Div>
      </Div>
      <Spacing lg="120" md="70" />
    </>
  );
}
